import { faGripDotsVertical, faPlus, faTrashCan } from '@fortawesome/pro-regular-svg-icons';
import React, { useEffect, useRef, useState } from 'react';
import { DndProvider, useDrag, useDrop } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { Controller, useFieldArray } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { EMPTY_PROMPT_PHASE, PromptPhase } from './ConfigSteps.tsx';
import { PromptTool, VoiceAgentPromptEditor } from './VoiceAgentPromptEditor.tsx';
import Button from '../../../core/components/Button.tsx';
import ConfirmModal from '../../../core/components/ConfirmModal.tsx';
import Disclosure from '../../../core/components/Disclosure.tsx';
import DropdownMenu from '../../../core/components/DropdownMenu.tsx';
import StringField from '../../../core/components/Fields/StringField.tsx';
import FormField from '../../../core/components/FormField.tsx';
import { withIcon } from '../../../core/components/Icon.tsx';
import { PROMPT_ADD_ON_TEMPLATES, PROMPT_FLOW_TEMPLATES } from '../constants.ts';
import { PROMPT_VARIABLES } from '../promptContext.ts';
import { phasesFromTemplate } from '../utils/editablePrompt.ts';

const AddIcon = withIcon(faPlus);
const DragIcon = withIcon(faGripDotsVertical);
const TrashIcon = withIcon(faTrashCan);

// react-dnd item type of a phase card; the list has its own DndProvider, like the other sortable lists
const PHASE_DRAG_TYPE = 'voice-agent-prompt-phase';

export interface FlowTemplate {
    key: string;
    addOn: boolean;
    phases: PromptPhase[];
}

const PHASE_TEXT_FIELDS = ['entry', 'procedure', 'response', 'nextStep'] as const;

// The grip and the delete button sit in the row's summary; a click on them must not open or close the phase
const keepClosedState = (event: React.MouseEvent) => event.preventDefault();

// One phase as a collapsible row: closed it shows its position and goal, open the goal and four texts with the
// variable and tool chips. A grip drags it to another position (same mechanics as the list-of-services position
// cards). The inputs stay registered under the form index for the whole drag; only the shown position changes
// until the drop.
const PromptPhaseCard = ({
    form,
    id,
    formIndex,
    position,
    tools,
    isOpen,
    onOpenChange,
    onDragMove,
    onDragEnd,
    onDelete,
}: {
    form: any;
    id: string;
    formIndex: number;
    position: number;
    tools: PromptTool[];
    isOpen: boolean;
    onOpenChange: (isOpen: boolean) => void;
    onDragMove: (id: string, position: number) => void;
    onDragEnd: (id: string, isDropped: boolean) => void;
    onDelete: () => void;
}) => {
    const { t } = useTranslation('voice_agents');
    const index = formIndex;
    const rowRef = useRef<HTMLDetailsElement>(null);
    const handleRef = useRef<HTMLSpanElement>(null);
    const goal: string = form.watch(`editablePrompt.phases.${index}.goal`) || '';

    const [, drop] = useDrop({
        accept: PHASE_DRAG_TYPE,
        hover(item: { id: string; index: number }, monitor) {
            if (!rowRef.current || item.index === position) return;
            // only move once the pointer crossed the middle of the hovered row, so the list doesn't flicker
            const rect = rowRef.current.getBoundingClientRect();
            const middleY = (rect.bottom - rect.top) / 2;
            const pointerY = (monitor.getClientOffset()?.y ?? 0) - rect.top;
            if ((item.index < position && pointerY < middleY) || (item.index > position && pointerY > middleY)) return;
            onDragMove(item.id, position);
            item.index = position;
        },
    });
    const [{ isDragging }, drag, preview] = useDrag({
        type: PHASE_DRAG_TYPE,
        item: { id, index: position },
        end: (item, monitor) => onDragEnd(item.id, monitor.didDrop()),
        collect: (monitor) => ({ isDragging: monitor.isDragging() }),
    });
    drag(handleRef);
    drop(preview(rowRef));

    return (
        <Disclosure
            ref={rowRef}
            open={isOpen}
            onOpenChange={onOpenChange}
            className={isDragging ? 'opacity-40' : undefined}
            title={
                <span className="inline-flex items-center gap-2">
                    <span
                        ref={handleRef}
                        role="button"
                        aria-label={t('form.prompt.flow.dragHandle')}
                        title={t('form.prompt.flow.dragHandle')}
                        className="-my-1 flex items-center rounded px-1 py-1 text-content-medium transition-colors duration-200 hover:bg-surface-strong cursor-move"
                        onClick={keepClosedState}
                    >
                        <DragIcon />
                    </span>
                    {/* the number is the position only; the goal stays an editable field, not a second title */}
                    {t('form.prompt.flow.phaseTitle', { number: position + 1 })}
                </span>
            }
            summary={goal || t('form.prompt.flow.untitledPhase')}
            aside={
                <Button
                    type="button"
                    variant="ghost"
                    className="-my-1.5 px-2 text-content-danger-weak"
                    onClick={(event) => {
                        keepClosedState(event);
                        onDelete();
                    }}
                    aria-label={t('form.prompt.flow.delete')}
                    title={t('form.prompt.flow.delete')}
                >
                    <TrashIcon />
                </Button>
            }
        >
            <div className="flex flex-col gap-3 px-1 pt-1">
                <Controller
                    name={`editablePrompt.phases.${index}.goal`}
                    control={form.control}
                    render={({ field: goalField }) => (
                        <FormField label={t('form.prompt.flow.fields.goal')}>
                            <StringField
                                value={goalField.value || ''}
                                onValueChange={goalField.onChange}
                                onBlur={goalField.onBlur}
                                placeholder={t('form.prompt.flow.fieldPlaceholders.goal')}
                            />
                        </FormField>
                    )}
                />
                <div className="grid gap-3 md:grid-cols-2">
                    {PHASE_TEXT_FIELDS.map((name) => (
                        <Controller
                            key={name}
                            name={`editablePrompt.phases.${index}.${name}`}
                            control={form.control}
                            render={({ field: textField }) => (
                                <FormField label={t(`form.prompt.flow.fields.${name}`)}>
                                    <VoiceAgentPromptEditor
                                        value={textField.value || ''}
                                        onValueChange={textField.onChange}
                                        variables={PROMPT_VARIABLES}
                                        tools={tools}
                                        placeholder={t(`form.prompt.flow.fieldPlaceholders.${name}`)}
                                        minHeightClass="min-h-18"
                                        palette={false}
                                    />
                                </FormField>
                            )}
                        />
                    ))}
                </div>
            </div>
        </Disclosure>
    );
};

// The conversation flow: one collapsible row per phase (goal plus four texts), dragged into order, deleted, or
// appended as an empty phase or as a copy of one template phase. Template texts are copied; the user edits the copy.
// Rows start closed so the flow reads as a list; an appended phase opens so it can be filled in.
const VoiceAgentPromptPhases = ({
    form,
    flowTemplates,
    tools,
}: {
    form: any;
    flowTemplates: FlowTemplate[];
    tools: PromptTool[];
}) => {
    const { t } = useTranslation('voice_agents');
    const { fields, append, remove, move } = useFieldArray({
        control: form.control,
        name: 'editablePrompt.phases',
    });
    const [pendingDelete, setPendingDelete] = useState<number | null>(null);
    // the order while a card is dragged; the form's move happens once on drop (as in the document fields config),
    // so the inputs are not re-registered under new names while the browser is still dragging
    const [dragOrder, setDragOrder] = useState<string[] | null>(null);
    const shownFields = dragOrder ? dragOrder.map((id) => fields.find((field) => field.id === id)!) : fields;

    const [openIds, setOpenIds] = useState<Set<string>>(new Set());
    const openAppended = useRef(false);
    const allOpen = fields.length > 0 && fields.every((field) => openIds.has(field.id));

    // the field array assigns the appended phase its id on the next render
    useEffect(() => {
        if (!openAppended.current || fields.length === 0) return;
        openAppended.current = false;
        const appendedId = fields[fields.length - 1].id;
        setOpenIds((ids) => new Set(ids).add(appendedId));
    }, [fields]);

    const appendPhase = (phase: PromptPhase | PromptPhase[]) => {
        openAppended.current = true;
        append(phase);
    };

    const setPhaseOpen = (id: string, isOpen: boolean) =>
        setOpenIds((ids) => {
            if (ids.has(id) === isOpen) return ids;
            const next = new Set(ids);
            if (isOpen) next.add(id);
            else next.delete(id);
            return next;
        });

    const handleDragMove = (id: string, position: number) =>
        setDragOrder((order) => {
            const current = order ?? fields.map((field) => field.id);
            const rest = current.filter((item) => item !== id);
            return [...rest.slice(0, position), id, ...rest.slice(position)];
        });

    const handleDragEnd = (id: string, isDropped: boolean) => {
        const from = fields.findIndex((field) => field.id === id);
        const to = dragOrder ? dragOrder.indexOf(id) : from;
        setDragOrder(null);
        if (isDropped && from !== to) move(from, to);
    };

    // every template phase is offered on its own, grouped by the flow it comes from — the user composes the flow
    const templatesByKey = Object.fromEntries(flowTemplates.map((template) => [template.key, template]));
    const templates = [...PROMPT_FLOW_TEMPLATES, ...PROMPT_ADD_ON_TEMPLATES]
        .map((key) => templatesByKey[key])
        .filter(Boolean);

    return (
        <div className="flex flex-col gap-3">
            <div className="flex items-start justify-between gap-4">
                <div className="flex flex-col gap-0.5">
                    <h4 className="text-sm font-medium text-content-strong">{t('form.prompt.flow.title')}</h4>
                    <span className="text-xs text-content-medium">{t('form.prompt.flow.helpText')}</span>
                </div>
                <div className="flex flex-none items-center gap-2">
                    {fields.length > 0 && (
                        <Button
                            type="button"
                            variant="ghost"
                            onClick={() => setOpenIds(allOpen ? new Set() : new Set(fields.map((field) => field.id)))}
                        >
                            {t(allOpen ? 'form.prompt.flow.collapseAll' : 'form.prompt.flow.expandAll')}
                        </Button>
                    )}
                    <DropdownMenu>
                        <DropdownMenu.Trigger asChild>
                            <Button type="button" variant="outline">
                                <AddIcon className="text-xs" />
                                {t('form.prompt.flow.addPhase')}
                            </Button>
                        </DropdownMenu.Trigger>
                        {/* a short, scrolling list: with every template phase the menu would be taller than the viewport */}
                        <DropdownMenu.Content
                            align="end"
                            className="min-w-72 max-h-80"
                            // above the create modal (z 21000), like the select field's menu; the max height makes it scroll
                            style={{ zIndex: 60000 }}
                        >
                            <DropdownMenu.Item
                                className="px-3 py-2 text-sm"
                                onClick={() => appendPhase({ ...EMPTY_PROMPT_PHASE })}
                            >
                                {t('form.prompt.flow.templates.empty')}
                            </DropdownMenu.Item>
                            {templates.map((template) => (
                                <React.Fragment key={template.key}>
                                    <DropdownMenu.Separator />
                                    <DropdownMenu.Label>
                                        {t(`form.prompt.flow.templates.${template.key}`)}
                                    </DropdownMenu.Label>
                                    {template.phases.map((phase, index) => (
                                        <DropdownMenu.Item
                                            key={`${template.key}-${index}`}
                                            className="px-3 py-2 text-sm"
                                            onClick={() => appendPhase(phasesFromTemplate([phase]))}
                                        >
                                            {phase.goal}
                                        </DropdownMenu.Item>
                                    ))}
                                </React.Fragment>
                            ))}
                        </DropdownMenu.Content>
                    </DropdownMenu>
                </div>
            </div>

            {fields.length === 0 && (
                <span className="text-xs text-content-danger-weak">{t('form.prompt.flow.noPhases')}</span>
            )}

            {fields.length > 0 && (
                <DndProvider backend={HTML5Backend}>
                    <Disclosure.Group>
                        {shownFields.map((field, position) => {
                            const formIndex = fields.indexOf(field);
                            return (
                                <PromptPhaseCard
                                    key={field.id}
                                    form={form}
                                    id={field.id}
                                    formIndex={formIndex}
                                    position={position}
                                    tools={tools}
                                    isOpen={openIds.has(field.id)}
                                    onOpenChange={(isOpen) => setPhaseOpen(field.id, isOpen)}
                                    onDragMove={handleDragMove}
                                    onDragEnd={handleDragEnd}
                                    onDelete={() => setPendingDelete(formIndex)}
                                />
                            );
                        })}
                    </Disclosure.Group>
                </DndProvider>
            )}

            {pendingDelete !== null && (
                <ConfirmModal
                    visible
                    title={t('form.prompt.flow.deleteConfirm.title')}
                    labelConfirm={t('form.prompt.flow.deleteConfirm.confirm')}
                    danger
                    onConfirm={() => {
                        remove(pendingDelete);
                        setPendingDelete(null);
                    }}
                    onCancel={() => setPendingDelete(null)}
                >
                    {t('form.prompt.flow.deleteConfirm.text')}
                </ConfirmModal>
            )}
        </div>
    );
};

export default VoiceAgentPromptPhases;
